import { useTranslation } from 'react-i18next'
import { IdCard, Modal, Stepper } from '@/components/molecules'
import type { StepperItem } from '@/components/molecules'
import { useOnboardingPetPage } from './page.onboarding.pet.hook'
import { PetStepOnePage } from './step-one/page.onboarding.pet-step-one'
import { PetStepTwoPage } from './step-two/page.onboarding.pet-step-two'
import { PetStepThreePage } from './step-three/page.onboarding.pet-step-three'
import { PetStepFourPage } from './step-four/page.onboarding.pet-step-four'
import { PetStepFivePage } from './step-five/page.onboarding.pet-step-five'
import { PetStepSixPage } from './step-six/page.onboarding.pet-step-six'
import { PaymentLayout } from '@/pages/payment/page.payment'
import { formatDate } from '@/shared/helpers/format-date'
import { petDefaultIdCardImage } from '@/shared/assets'

export function OnboardingPetPage() {
  const { t } = useTranslation()
  const {
    isModalOpen,
    closeModal,
    currentStep,
    isPolicyDataLoading,
    isCompleting,
    goToNextStep,
    goToPreviousStep,
    completeOnboarding,
    onboardingPet,
    onboardingPolicy,
    user,
    isEnglish,
  } = useOnboardingPetPage()

  const steps: StepperItem[] = [
    {
      label: t('onboarding.pet.stepper.step1'),
      children: (
        <PetStepOnePage
          onContinue={goToNextStep}
          pet={onboardingPet}
          policy={onboardingPolicy}
          isLoading={isPolicyDataLoading}
        />
      ),
    },
    {
      label: t('onboarding.pet.stepper.step2'),
      children: (
        <PetStepTwoPage onContinue={goToNextStep} onBack={goToPreviousStep} />
      ),
    },
    {
      label: t('onboarding.pet.stepper.step3'),
      children: (
        <PetStepThreePage
          onContinue={goToNextStep}
          onBack={goToPreviousStep}
          policy={onboardingPolicy}
        />
      ),
    },
    {
      label: t('onboarding.pet.stepper.step4'),
      children: (
        <PetStepFourPage onContinue={goToNextStep} onBack={goToPreviousStep} />
      ),
    },
    {
      label: t('onboarding.pet.stepper.step5'),
      children: (
        <PetStepFivePage onContinue={goToNextStep} onBack={goToPreviousStep} />
      ),
    },
    {
      label: t('onboarding.pet.stepper.step6'),
      children: (
        <PetStepSixPage
          onBack={goToPreviousStep}
          onContinue={completeOnboarding}
          isLoading={isCompleting}
        />
      ),
    },
  ]

  return (
    <>
      <PaymentLayout />
      <Modal
        isOpen={isModalOpen}
        className={`flex h-full justify-center max-md:max-h-[calc(100dvh-var(--modal-mobile-offset)-var(--sat))] md:h-full ${isEnglish ? 'md:max-h-131.5' : 'md:max-h-142'} md:max-w-237`}
        chilrenClassName="max-h-auto"
        onClose={closeModal}
      >
        <div
          className="flex h-full w-full flex-col px-6 py-8 md:pt-19.5 md:pr-16.25 md:pb-17 md:pl-24.5"
          data-testid="onboarding-pet-modal"
        >
          <div
            className={`grid place-items-center overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-in-out md:hidden ${
              currentStep === 0
                ? 'grid-rows-[1fr] pt-8 pb-3 opacity-100'
                : 'grid-rows-[0fr] p-0 opacity-0'
            }`}
          >
            <div className="min-h-0 w-full">
              <IdCard
                variant="with-photo"
                planName={onboardingPet?.name ?? ''}
                ownerName={user?.fullName ?? ''}
                policyId={
                  onboardingPolicy?.policyExternalId
                    ? `#${onboardingPolicy.policyExternalId}`
                    : ''
                }
                startDate={formatDate(onboardingPolicy?.startDate)}
                photoUrl={
                  isPolicyDataLoading
                    ? undefined
                    : onboardingPet?.petPictureUrl
                      ? onboardingPet?.petPictureUrl
                      : petDefaultIdCardImage
                }
                isLoading={isPolicyDataLoading}
                isMobile={false}
                hidePhotoBackground={
                  !isPolicyDataLoading && !onboardingPet?.petPictureUrl
                }
                className="mx-auto aspect-auto max-h-51.5 w-full max-w-81.5 sm:aspect-326/206"
              />
            </div>
          </div>
          <Stepper
            items={steps}
            currentStep={currentStep}
            className="min-h-0 flex-1 md:gap-0"
          />
        </div>
      </Modal>
    </>
  )
}
